import { useEffect, useState } from "react";
import { publicApi, api, TOKEN_KEY } from "@/lib/api";

// ---------------------------------------------------------------------------
// Content store keys
// ---------------------------------------------------------------------------
export const HOME_PUBLISHED_KEY = "home_builder";
export const HOME_DRAFT_KEY = "home_builder_draft";

// ---------------------------------------------------------------------------
// Lightweight, block-based CMS definitions for the public site.
//
// The public pages render a persisted, ordered array of "blocks". Each block
// maps to an EXISTING, already-styled section component — the CMS only lets an
// admin reorder blocks, toggle visibility, and edit their text/image content.
// It never changes the visual design (layout / fonts / colors / spacing).
//
// DEFAULT_* below MUST reproduce the current public pages pixel-for-pixel so
// that a fresh site (no saved content) looks identical to today.
// ---------------------------------------------------------------------------

// Icons available for the Trust Highlights section (keys map to lucide-react
// components inside the public renderer).
export const ICON_OPTIONS = [
  { value: "shield", label: "Shield (authentication)" },
  { value: "gem", label: "Gem (sourcing)" },
  { value: "lock", label: "Lock (discretion)" },
  { value: "crown", label: "Crown (VIP)" },
  { value: "watch", label: "Watch" },
  { value: "star", label: "Star" },
];

// ---------------------------------------------------------------------------
// HOMEPAGE
// ---------------------------------------------------------------------------
export const DEFAULT_HOME_BLOCKS = [
  {
    id: "hero",
    type: "hero",
    enabled: true,
    content: {
      line1: "The watches you want.",
      line2: "Handled the way they should be.",
      subtitle:
        "A private dealership for serious collectors \u2014 authenticated inventory, discreet sourcing, and a standing seat at the table for those on the list.",
      cta_label: "Browse the Collection",
      cta_link: "/watches",
      secondary_label: "Already a client? Sign in",
      secondary_link: "/account/login",
      image: "https://images.unsplash.com/photo-1547996160-81dfa63595aa?w=1920&q=85",
      image_alt: "Rolex Oyster Perpetual Air-King",
    },
  },
  {
    id: "trust",
    type: "trust",
    enabled: true,
    content: {
      items: [
        {
          icon: "shield",
          title: "Authentication-first",
          body: "Every piece inspected and verified before it changes hands \u2014 and after delivery.",
        },
        {
          icon: "gem",
          title: "Sourced with intent",
          body: "Grail-tier and everyday icons, sourced privately and matched to your taste.",
        },
        {
          icon: "lock",
          title: "Handled with discretion",
          body: "Private inventory, quiet transactions, white-glove logistics end to end.",
        },
      ],
    },
  },
  {
    id: "military",
    type: "military",
    enabled: true,
    content: {
      eyebrow: "HONORING THOSE WHO SERVE",
      title: "Military & Veterans receive 10% off.",
      body:
        "We're proud to support active-duty service members, veterans, and their families. Verify your service securely with ID.me and your 10% discount is applied automatically at checkout.",
      button_label: "Verify with ID.me",
    },
  },
  {
    id: "top_picks",
    type: "top_picks",
    enabled: true,
    content: {
      eyebrow: "CURATED BY THE HOUSE",
      title: "Top Picks",
      view_all_label: "View all watches \u2192",
    },
  },
  {
    id: "vip_tiers",
    type: "vip_tiers",
    enabled: true,
    content: {
      eyebrow: "THE VIP LEDGER",
      title: "Access is earned. Start at the front door.",
      subtitle:
        "Join free and we place you on the ledger. Your engagement and taste move you up \u2014 toward private previews, first calls on grails, and the inner circle.",
      cta_label: "Claim Your Place on the Ledger",
    },
  },
];

// Editor schema: describes which fields each block type exposes in the admin
// CMS. `type` drives which input control is rendered.
export const HOME_BLOCK_TYPES = {
  hero: {
    label: "Hero",
    description: "Full-screen banner with headline, subtitle and primary call-to-action.",
    fields: [
      { key: "line1", label: "Headline \u2014 line 1", type: "text" },
      { key: "line2", label: "Headline \u2014 line 2 (italic accent)", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea", rows: 3 },
      { key: "cta_label", label: "Button label", type: "text" },
      { key: "cta_link", label: "Button link (path)", type: "text" },
      { key: "secondary_label", label: "Secondary link label", type: "text" },
      { key: "secondary_link", label: "Secondary link (path)", type: "text" },
      { key: "image", label: "Background image", type: "image" },
      { key: "image_alt", label: "Background image alt text", type: "text" },
    ],
  },
  trust: {
    label: "Trust Highlights",
    description: "Three-column strip of trust points shown under the hero.",
    fields: [
      {
        key: "items",
        label: "Highlights",
        type: "list",
        itemLabel: "Highlight",
        min: 1,
        max: 4,
        newItem: { icon: "star", title: "", body: "" },
        itemFields: [
          { key: "icon", label: "Icon", type: "select", options: ICON_OPTIONS },
          { key: "title", label: "Title", type: "text" },
          { key: "body", label: "Description", type: "textarea", rows: 2 },
        ],
      },
    ],
  },
  military: {
    label: "Military & Veterans Banner",
    description: "The military / veterans discount banner with ID.me verification.",
    fields: [
      { key: "eyebrow", label: "Eyebrow (small caps)", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Body", type: "textarea", rows: 3 },
      { key: "button_label", label: "Button label", type: "text" },
    ],
  },
  top_picks: {
    label: "Top Picks",
    description:
      "Featured watches. Products are selected AUTOMATICALLY \u2014 you control the heading text and whether the section is shown.",
    auto: true,
    fields: [
      { key: "eyebrow", label: "Eyebrow (small caps)", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "view_all_label", label: "\u201cView all\u201d link label", type: "text" },
    ],
  },
  vip_tiers: {
    label: "VIP Ledger / Tiers",
    description: "The VIP tier ladder. Tiers are loaded automatically; you control the intro copy.",
    auto: true,
    fields: [
      { key: "eyebrow", label: "Eyebrow (small caps)", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea", rows: 3 },
      { key: "cta_label", label: "Button label", type: "text" },
    ],
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Deep clone that is safe for our plain-JSON block structures.
export function cloneBlocks(blocks) {
  return JSON.parse(JSON.stringify(blocks));
}

// Merge a persisted blocks array with the defaults. Guarantees:
//  - unknown block types are dropped
//  - each block's content falls back to defaults for any missing keys
//  - if nothing valid is saved, the pristine defaults are returned
export function mergeBlocks(saved, defaults, typeSchema) {
  if (!Array.isArray(saved) || saved.length === 0) return cloneBlocks(defaults);
  const defByType = Object.fromEntries(defaults.map((b) => [b.type, b.content]));
  const merged = saved
    .filter((b) => b && typeSchema[b.type])
    .map((b, i) => ({
      id: b.id || `${b.type}-${i}`,
      type: b.type,
      enabled: b.enabled !== false,
      content: { ...(defByType[b.type] || {}), ...(b.content || {}) },
    }));
  return merged.length ? merged : cloneBlocks(defaults);
}

export function mergeHomeBlocks(saved) {
  return mergeBlocks(saved, DEFAULT_HOME_BLOCKS, HOME_BLOCK_TYPES);
}


// ---------------------------------------------------------------------------
// OTHER PAGE INTROS (VIP / Bespoke / Sell / Journal)
//
// Each page's header/intro copy is CMS-editable. Defaults MUST reproduce the
// current public copy so nothing changes visually until an admin edits it.
// (The About page has its own dedicated editor at /admin/about.)
// ---------------------------------------------------------------------------
export const PAGE_INTROS = {
  vip: {
    label: "VIP",
    key: "vip_intro",
    livePath: "/vip",
    description: "Header intro on the \u201cJoin the VIP List\u201d page.",
    fields: [
      { key: "eyebrow", label: "Eyebrow (small caps)", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea", rows: 2 },
    ],
    defaults: {
      eyebrow: "JOIN THE VIP LIST",
      title: "Claim your place on the ledger.",
      subtitle: "Tell us what you collect. The more we know, the better we serve.",
    },
  },
  bespoke: {
    label: "Bespoke",
    key: "bespoke_intro",
    livePath: "/bespoke",
    description: "Hero intro on the Bespoke / Private Sourcing page.",
    fields: [
      { key: "eyebrow", label: "Eyebrow (small caps)", type: "text" },
      { key: "title_line1", label: "Title \u2014 line 1", type: "text" },
      { key: "title_line2", label: "Title \u2014 line 2 (italic accent)", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea", rows: 3 },
      { key: "image", label: "Hero background image", type: "image" },
    ],
    defaults: {
      eyebrow: "Private Sourcing",
      title_line1: "The bespoke commission.",
      title_line2: "For the piece that isn\u2019t listed.",
      subtitle:
        "Tell us the grail. We put a discreet global network to work \u2014 sourcing, authenticating, and delivering the exact reference, specification, and condition you have in mind.",
      image: "https://images.unsplash.com/photo-1509048191080-d2984bad6ae5?w=1920&q=80",
    },
  },
  sell: {
    label: "Sell",
    key: "sell_intro",
    livePath: "/sell",
    description: "Header intro on the Sell / Consign page.",
    fields: [
      { key: "eyebrow", label: "Eyebrow (small caps)", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea", rows: 2 },
    ],
    defaults: {
      eyebrow: "SELL OR CONSIGN",
      title: "Place a piece with the house.",
      subtitle: "Secure, professional, and protected on both sides. Tell us about your watch.",
    },
  },
  journal: {
    label: "Journal",
    key: "journal_intro",
    livePath: "/journal",
    description: "Header intro on The Journal index page.",
    fields: [
      { key: "eyebrow", label: "Eyebrow (small caps)", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea", rows: 2 },
    ],
    defaults: {
      eyebrow: "THE JOURNAL",
      title: "Watch Guides & Notes",
      subtitle:
        "Authentication, collecting, and the pieces worth knowing \u2014 written the way we'd tell you across the table.",
    },
  },
};

// React hook: load a page's intro content merged over defaults.
// Returns [intro, preview]. When the URL has ?preview=1 AND a valid admin token
// is present, the admin-only DRAFT is loaded (private draft preview) and
// preview=true. Otherwise the published content is used. Public visitors never
// see drafts — the draft lives behind an authenticated admin endpoint.
// Always returns something renderable; falls back to defaults on any error so
// the public pages never break.
const _CONTENT_META = ["key", "updated_at", "updated_by", "updated_by_name", "_id"];

export function usePageIntro(key, defaults) {
  const [intro, setIntro] = useState(defaults);
  const [preview, setPreview] = useState(false);
  useEffect(() => {
    let alive = true;
    const loadPublished = () =>
      publicApi
        .get(`/public/content/${key}`)
        .then((r) => { if (alive) setIntro({ ...defaults, ...(r.data || {}) }); })
        .catch(() => { if (alive) setIntro(defaults); });

    const wantsPreview =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("preview") === "1";
    const adminToken =
      typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

    if (wantsPreview && adminToken) {
      api
        .get(`/admin/content/${key}_draft`)
        .then((r) => {
          if (!alive) return;
          const d = r.data || {};
          const hasContent = Object.keys(d).some((k) => !_CONTENT_META.includes(k));
          if (hasContent) { setIntro({ ...defaults, ...d }); setPreview(true); }
          else loadPublished();
        })
        .catch(loadPublished);
    } else {
      loadPublished();
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return [intro, preview];
}

// ---------------------------------------------------------------------------
// SITE NAVIGATION & FOOTER (header nav, slide-in menu, footer links + text)
// Defaults MUST reproduce the current PublicLayout exactly.
// ---------------------------------------------------------------------------
export const SITE_NAV_KEY = "site_nav";

export const DEFAULT_SITE_NAV = {
  primary: [
    { label: "Timepieces", to: "/watches" },
    { label: "Parts", to: "/parts" },
    { label: "Shop", to: "/shop" },
    { label: "Bespoke", to: "/bespoke" },
    { label: "Journal", to: "/journal" },
  ],
  more: [
    { label: "Home", to: "/" },
    { label: "The VIP List", to: "/vip" },
    { label: "Sell / Consign", to: "/sell" },
    { label: "The House", to: "/about" },
    { label: "Veterans\u2019 Circle", to: "/veterans" },
  ],
  footer_links: [
    { label: "Our Story", to: "/about" },
    { label: "Shop", to: "/shop" },
    { label: "Bespoke Sourcing", to: "/bespoke" },
    { label: "Join the VIP List", to: "/vip" },
    { label: "Veterans\u2019 Circle", to: "/veterans" },
  ],
  footer: {
    tagline: "Authenticated luxury timepieces. Sourced with discipline, delivered with discretion.",
    house_title: "The House",
    promise_title: "Promise",
    promise_body: "Authentication-first. Every piece is inspected and verified before it changes hands.",
    military_note: "Military & Veteran discount available.",
    copyright: "LE COLLECTIF HORLOGER \u00b7 ALL RIGHTS RESERVED",
  },
  announcement: {
    lead: "Honoring those who serve \u2014 ",
    highlight: "Military & Veterans receive 10% off",
    tail: ", verified securely at checkout.",
  },
};

export function mergeSiteNav(saved) {
  const d = DEFAULT_SITE_NAV;
  if (!saved || typeof saved !== "object") return JSON.parse(JSON.stringify(d));
  return {
    primary: Array.isArray(saved.primary) && saved.primary.length ? saved.primary : d.primary,
    more: Array.isArray(saved.more) && saved.more.length ? saved.more : d.more,
    footer_links: Array.isArray(saved.footer_links) && saved.footer_links.length ? saved.footer_links : d.footer_links,
    footer: { ...d.footer, ...(saved.footer || {}) },
    announcement: { ...d.announcement, ...(saved.announcement || {}) },
  };
}

// Public hook: load nav/footer config with safe fallback to defaults.
export function useSiteNav() {
  const [nav, setNav] = useState(DEFAULT_SITE_NAV);
  useEffect(() => {
    let alive = true;
    publicApi
      .get(`/public/content/${SITE_NAV_KEY}`)
      .then((r) => { if (alive) setNav(mergeSiteNav(r.data)); })
      .catch(() => { if (alive) setNav(DEFAULT_SITE_NAV); });
    return () => { alive = false; };
  }, []);
  return nav;
}
